import React, { useState, useEffect } from 'react';
import { Terminal, Activity, Settings, AlertCircle, DollarSign, CheckCircle, Clock, Users, AlertTriangle, XCircle, Search, Key, Trash2, RefreshCw, Lock, Shield } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config/API_URL';
import FeaturesManagement from './SubscriptionFeaturesPage';
import SystemPage from './SystemPage';
import AIManagementPage from './AIManagement';
import AccessCodesPage from './AccessCodesPage';
import SchedulerPage from './SchedulerPage';
import ContactSchedulerAdmin from './ContactSchedulerAdmin';

// Login Modal Component
const AdminLoginModal = ({ onLoginSuccess }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Call the new login endpoint
      const response = await fetch(`${API_URL}/admin/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password })
      });

      const data = await response.json();

      if (response.ok) {
        onLoginSuccess();
      } else {
        setError(data.error || 'Invalid admin password');
      }
    } catch (err) {
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(10, 10, 15, 0.95)',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
    },
    modal: {
      background: 'rgba(20, 20, 35, 0.95)',
      backdropFilter: 'blur(20px)',
      borderRadius: '20px',
      padding: '3rem',
      border: '1px solid rgba(0, 255, 245, 0.3)',
      boxShadow: '0 20px 60px rgba(0, 255, 245, 0.2)',
      maxWidth: '450px',
      width: '90%',
      position: 'relative',
    },
    iconContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '1.5rem',
    },
    icon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, rgba(0, 255, 245, 0.2), rgba(139, 92, 246, 0.2))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px solid #00fff5',
      boxShadow: '0 0 30px rgba(0, 255, 245, 0.4)',
    },
    title: {
      fontSize: '2rem',
      fontWeight: 900,
      fontFamily: "'Orbitron', monospace",
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textAlign: 'center',
      marginBottom: '0.5rem',
      letterSpacing: '2px',
    },
    subtitle: {
      fontSize: '1rem',
      color: '#a0a0c0',
      textAlign: 'center',
      marginBottom: '2rem',
      fontFamily: "'Rajdhani', sans-serif",
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
    },
    inputGroup: {
      position: 'relative',
    },
    label: {
      display: 'block',
      fontSize: '0.9rem',
      color: '#a0a0c0',
      marginBottom: '0.5rem',
      fontWeight: 600,
      fontFamily: "'Rajdhani', sans-serif",
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
    input: {
      width: '100%',
      padding: '1rem 1rem 1rem 3rem',
      background: 'rgba(10, 10, 15, 0.8)',
      border: '1px solid rgba(0, 255, 245, 0.3)',
      borderRadius: '12px',
      color: '#e0e0ff',
      fontSize: '1rem',
      outline: 'none',
      transition: 'all 0.3s ease',
      fontFamily: "'Rajdhani', sans-serif",
      boxSizing: 'border-box',
    },
    inputIcon: {
      position: 'absolute',
      left: '1rem',
      bottom: '1rem',
      color: '#00fff5',
    },
    button: {
      padding: '1rem',
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      border: 'none',
      borderRadius: '12px',
      color: '#0a0a0f',
      fontSize: '1rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      fontFamily: "'Orbitron', monospace",
      letterSpacing: '1px',
      textTransform: 'uppercase',
      boxShadow: '0 4px 20px rgba(0, 255, 245, 0.3)',
    },
    error: {
      background: 'rgba(239, 68, 68, 0.2)',
      border: '1px solid #ef4444',
      borderRadius: '8px',
      padding: '0.75rem 1rem',
      color: '#ff3366',
      fontSize: '0.9rem',
      fontFamily: "'Rajdhani', sans-serif",
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
  };

  return (
    <motion.div
      style={styles.overlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        style={styles.modal}
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        <div style={styles.iconContainer}>
          <div style={styles.icon}>
            <Shield size={40} style={{ color: '#00fff5' }} />
          </div>
        </div>

        <h1 style={styles.title}>ADMIN ACCESS</h1>
        <p style={styles.subtitle}>
          Authentication required to access admin dashboard
        </p>

        <form style={styles.form} onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Admin Password</label>
            <Lock size={20} style={styles.inputIcon} />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              style={styles.input}
              autoFocus
              disabled={loading}
            />
          </div>

          {error && (
            <motion.div
              style={styles.error}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <AlertCircle size={18} />
              {error}
            </motion.div>
          )}

          <motion.button
            type="submit"
            style={styles.button}
            disabled={loading || !password}
            whileHover={{ scale: 1.02, boxShadow: '0 6px 30px rgba(0, 255, 245, 0.5)' }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? 'AUTHENTICATING...' : 'ACCESS DASHBOARD'}
          </motion.button>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Toast Component
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    container: {
      position: 'fixed',
      top: '2rem',
      right: '2rem',
      background: type === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
      padding: '1rem 1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      zIndex: 10000,
      border: `1px solid ${type === 'success' ? '#10b981' : '#ef4444'}`,
      minWidth: '300px',
      color: 'white',
    },
    icon: {
      flexShrink: 0,
    },
    message: {
      flex: 1,
      fontSize: '0.95rem',
      fontWeight: 500,
    },
  };

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      style={styles.container}
    >
      {type === 'success' ? <CheckCircle size={24} /> : <XCircle size={24} />}
      <span style={styles.message}>{message}</span>
    </motion.div>
  );
};

// Navigation Bar Component
const NavigationBar = ({ activeTab, onTabChange }) => {
  const tabs = [
    { id: 'logs', label: 'System Logs', icon: Terminal },
    { id: 'system', label: 'System', icon: Settings },
    { id: 'scheduler', label: 'Scheduler', icon: Clock },
    { id: 'contact-scheduler', label: 'Contact Scheduler', icon: Users }, // ADD THIS
    { id: 'features', label: 'Features & Revenue', icon: DollarSign },
    { id: 'access-codes', label: 'Access Codes', icon: Key },
    { id: 'ai', label: 'AI', icon: Activity },
  ];

  const styles = {
    nav: {
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      padding: '0.5rem',
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '2rem',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      boxShadow: '0 4px 30px rgba(0, 255, 245, 0.1)',
    },
    tab: {
      flex: 1,
      padding: '1rem 1.5rem',
      background: 'transparent',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      color: '#a0a0c0',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: "'Rajdhani', sans-serif",
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
  };

  return (
    <nav style={styles.nav}>
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        
        return (
          <motion.button
            key={tab.id}
            style={{
              ...styles.tab,
              background: isActive ? 'linear-gradient(135deg, rgba(0, 255, 245, 0.1), rgba(139, 92, 246, 0.1))' : 'transparent',
              color: isActive ? '#00fff5' : '#a0a0c0',
              border: isActive ? '1px solid #00fff5' : '1px solid transparent',
              boxShadow: isActive ? '0 0 20px rgba(0, 255, 245, 0.3)' : 'none',
            }}
            onClick={() => onTabChange(tab.id)}
            whileHover={{
              scale: 1.02,
              backgroundColor: isActive ? undefined : 'rgba(255, 255, 255, 0.05)',
            }}
            whileTap={{ scale: 0.98 }}
          >
            <Icon size={20} />
            {tab.label}
          </motion.button>
        );
      })}
    </nav>
  );
};

// Log Level Badge Component
const LogLevelBadge = ({ level }) => {
  const getStyle = () => {
    switch (level) {
      case 'ERROR':
      case 'CRITICAL':
        return { bg: 'rgba(239, 68, 68, 0.2)', color: '#ff3366', border: '#ff3366' };
      case 'WARNING':
        return { bg: 'rgba(255, 170, 0, 0.2)', color: '#ffaa00', border: '#ffaa00' };
      case 'INFO':
        return { bg: 'rgba(0, 255, 245, 0.2)', color: '#00fff5', border: '#00fff5' };
      case 'DEBUG':
        return { bg: 'rgba(139, 92, 246, 0.2)', color: '#8b5cf6', border: '#8b5cf6' };
      default:
        return { bg: 'rgba(160, 160, 192, 0.2)', color: '#a0a0c0', border: '#a0a0c0' };
    }
  };

  const style = getStyle();

  return (
    <span style={{
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      letterSpacing: '1px',
      background: style.bg,
      color: style.color,
      border: `1px solid ${style.border}`,
      textShadow: `0 0 10px ${style.color}`,
    }}>
      {level}
    </span>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color, trend }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(20, 20, 35, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        padding: '1.5rem',
        border: `1px solid ${color}30`,
        boxShadow: `0 4px 30px ${color}20`,
        position: 'relative',
        overflow: 'hidden',
      }}
      whileHover={{
        scale: 1.02,
        boxShadow: `0 8px 40px ${color}30`,
      }}
    >
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '150px',
        height: '150px',
        background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
        borderRadius: '50%',
      }} />
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.75rem',
        }}>
          <span style={{
            fontSize: '0.9rem',
            color: '#a0a0c0',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontFamily: "'Rajdhani', sans-serif",
          }}>
            {title}
          </span>
          <Icon size={24} style={{ color: color }} />
        </div>
        
        <div style={{
          fontSize: '2.5rem',
          fontWeight: 900,
          fontFamily: "'Orbitron', monospace",
          background: `linear-gradient(135deg, ${color}, ${color}80)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '0.5rem',
        }}>
          {value}
        </div>
        
        {trend && (
          <div style={{
            fontSize: '0.85rem',
            color: trend > 0 ? '#00ff88' : '#ff3366',
            fontWeight: 600,
          }}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% from last hour
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Logs Page Component
const LogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('ALL');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  useEffect(() => {
    loadLogs();
    loadStats();
    const interval = setInterval(() => {
      loadLogs();
      loadStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [levelFilter, searchTerm]);

  const loadLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (levelFilter !== 'ALL') params.append('level', levelFilter);
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', '100');

      const response = await fetch(`${API_URL}/admin/logs?${params}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }
      
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Failed to load logs:', error);
      setToastMessage('Failed to load logs');
      setToastType('error');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/logs/stats`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all logs? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/logs/clear`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setToastMessage('Logs cleared successfully');
        setToastType('success');
        setShowToast(true);
        loadLogs();
        loadStats();
      } else {
        throw new Error('Failed to clear logs');
      }
    } catch (error) {
      console.error('Failed to clear logs:', error);
      setToastMessage('Failed to clear logs');
      setToastType('error');
      setShowToast(true);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    loadLogs();
    loadStats();
  };

  const styles = {
    container: {
      padding: '0',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '0.5rem',
      letterSpacing: '2px',
      textShadow: '0 0 20px rgba(0, 255, 245, 0.5)',
    },
    subtitle: {
      fontSize: '1.1rem',
      color: '#a0a0c0',
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: 500,
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    controlsBar: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
    },
    searchBox: {
      flex: 1,
      minWidth: '250px',
      position: 'relative',
    },
    searchInput: {
      width: '100%',
      padding: '0.875rem 1rem 0.875rem 3rem',
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      borderRadius: '12px',
      color: '#e0e0ff',
      fontSize: '1rem',
      outline: 'none',
      transition: 'all 0.3s ease',
      fontFamily: "'Rajdhani', sans-serif",
      boxSizing: 'border-box',
    },
    searchIcon: {
      position: 'absolute',
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#00fff5',
    },
    filterSelect: {
      padding: '0.875rem 1rem',
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      borderRadius: '12px',
      color: '#e0e0ff',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      outline: 'none',
      fontFamily: "'Orbitron', monospace",
      letterSpacing: '1px',
    },
    button: {
      padding: '0.875rem 1.5rem',
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 255, 245, 0.3)',
      borderRadius: '12px',
      color: '#00fff5',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontFamily: "'Rajdhani', sans-serif",
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    logsContainer: {
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      boxShadow: '0 4px 30px rgba(0, 255, 245, 0.1)',
      overflow: 'hidden',
    },
    logEntry: {
      padding: '1rem 1.5rem',
      borderBottom: '1px solid rgba(0, 255, 245, 0.1)',
      transition: 'all 0.3s ease',
      fontFamily: "'Rajdhani', sans-serif",
    },
    logTimestamp: {
      fontSize: '0.85rem',
      color: '#8b5cf6',
      fontFamily: "'Orbitron', monospace",
      marginBottom: '0.5rem',
      fontWeight: 600,
    },
    logMessage: {
      fontSize: '0.95rem',
      color: '#e0e0ff',
      lineHeight: 1.6,
      wordBreak: 'break-word',
      fontFamily: "'Rajdhani', monospace",
    },
    emptyState: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#a0a0c0',
    },
    emptyIcon: {
      marginBottom: '1rem',
      color: '#8b5cf6',
    },
  };

  if (loading && !logs.length) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.emptyState, padding: '6rem 2rem' }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <RefreshCw size={48} style={styles.emptyIcon} />
          </motion.div>
          <div style={{ fontSize: '1.2rem', marginTop: '1rem', fontFamily: "'Rajdhani', sans-serif" }}>
            Loading system logs...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <AnimatePresence>
        {showToast && (
          <Toast
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
          />
        )}
      </AnimatePresence>

      <div style={styles.header}>
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.title}
        >
          SYSTEM LOGS
        </motion.h1>
        <p style={styles.subtitle}>
          Real-time application monitoring and error tracking
        </p>
      </div>

      {stats && (
        <div style={styles.statsGrid}>
          <StatsCard
            title="Total Logs"
            value={stats.total.toLocaleString()}
            icon={Terminal}
            color="#00fff5"
          />
          <StatsCard
            title="Errors"
            value={stats.by_level.ERROR + stats.by_level.CRITICAL}
            icon={AlertCircle}
            color="#ff3366"
          />
          <StatsCard
            title="Warnings"
            value={stats.by_level.WARNING}
            icon={AlertTriangle}
            color="#ffaa00"
          />
          <StatsCard
            title="Info"
            value={stats.by_level.INFO}
            icon={CheckCircle}
            color="#00ff88"
          />
        </div>
      )}

      <div style={styles.controlsBar}>
        <div style={styles.searchBox}>
          <Search size={20} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="ALL">ALL LEVELS</option>
          <option value="DEBUG">DEBUG</option>
          <option value="INFO">INFO</option>
          <option value="WARNING">WARNING</option>
          <option value="ERROR">ERROR</option>
          <option value="CRITICAL">CRITICAL</option>
        </select>

        <motion.button
          style={styles.button}
          onClick={handleRefresh}
          whileHover={{
            scale: 1.05,
            boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)',
          }}
          whileTap={{ scale: 0.95 }}
        >
          <RefreshCw size={18} />
          Refresh
        </motion.button>

        <motion.button
          style={{
            ...styles.button,
            border: '1px solid rgba(255, 51, 102, 0.3)',
            color: '#ff3366',
          }}
          onClick={handleClearLogs}
          whileHover={{
            scale: 1.05,
            boxShadow: '0 0 20px rgba(255, 51, 102, 0.4)',
          }}
          whileTap={{ scale: 0.95 }}
        >
          <Trash2 size={18} />
          Clear Logs
        </motion.button>
      </div>

      <div style={styles.logsContainer}>
        {logs.length === 0 ? (
          <div style={styles.emptyState}>
            <Terminal size={64} style={styles.emptyIcon} />
            <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', fontFamily: "'Orbitron', monospace" }}>
              No logs found
            </h3>
            <p style={{ fontSize: '1rem', fontFamily: "'Rajdhani', sans-serif" }}>
              {searchTerm || levelFilter !== 'ALL'
                ? 'Try adjusting your search criteria'
                : 'System logs will appear here'}
            </p>
          </div>
        ) : (
          logs.map((log, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.02 }}
              style={styles.logEntry}
              whileHover={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                marginBottom: '0.5rem',
              }}>
                <LogLevelBadge level={log.level} />
                <div style={styles.logTimestamp}>{log.timestamp}</div>
              </div>
              <div style={styles.logMessage}>{log.message}</div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('logs');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check authentication on mount
  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/test`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#0a0a0f',
      color: '#e0e0ff',
      position: 'relative',
      overflow: 'hidden',
    },
    background: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 0,
      background: `
        radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(0, 255, 245, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 40% 80%, rgba(255, 0, 110, 0.1) 0%, transparent 50%)
      `,
    },
    grid: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 0,
      backgroundImage: `
        linear-gradient(rgba(0, 255, 245, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(0, 255, 245, 0.03) 1px, transparent 1px)
      `,
      backgroundSize: '50px 50px',
      opacity: 0.3,
    },
    navbar: {
      background: 'rgba(20, 20, 35, 0.8)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(0, 255, 245, 0.3)',
      padding: '1rem 2rem',
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      boxShadow: '0 4px 30px rgba(0, 255, 245, 0.1)',
    },
    navbarContent: {
      maxWidth: '1600px',
      margin: '0 auto',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logo: {
      fontFamily: "'Orbitron', monospace",
      fontSize: '1.5rem',
      fontWeight: 900,
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      letterSpacing: '2px',
    },
    content: {
      maxWidth: '1600px',
      margin: '0 auto',
      padding: '2rem',
      position: 'relative',
      zIndex: 1,
    },
    blurredContent: {
      filter: 'blur(10px)',
      pointerEvents: 'none',
      userSelect: 'none',
    },
  };

  // Show loading state while checking authentication
  if (checkingAuth) {
    return (
      <>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700;900&family=Rajdhani:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        
        <div style={styles.container}>
          <div style={styles.background} />
          <div style={styles.grid} />
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            color: '#00fff5',
            fontFamily: "'Orbitron', monospace",
            fontSize: '1.5rem',
          }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            >
              <RefreshCw size={48} />
            </motion.div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700;900&family=Rajdhani:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      
      <div style={styles.container}>
        <div style={styles.background} />
        <div style={styles.grid} />
        
        {/* Show login modal if not authenticated */}
        <AnimatePresence>
          {!isAuthenticated && (
            <AdminLoginModal onLoginSuccess={handleLoginSuccess} />
          )}
        </AnimatePresence>
        
        {/* Main content - blurred when not authenticated */}
        <div style={!isAuthenticated ? styles.blurredContent : {}}>
          <div style={styles.navbar}>
            <div style={styles.navbarContent}>
              <motion.div
                style={styles.logo}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                ADMIN COMMAND CENTER
              </motion.div>
              <div style={{
                fontSize: '0.9rem',
                color: '#a0a0c0',
                fontFamily: "'Rajdhani', sans-serif",
                fontWeight: 500,
              }}>
                System Status: <span style={{ color: '#00ff88' }}>ONLINE</span>
              </div>
            </div>
          </div>

          <div style={styles.content}>
            <NavigationBar activeTab={activeTab} onTabChange={setActiveTab} />
            
            <AnimatePresence mode="wait">
              {activeTab === 'logs' && <LogsPage key="logs" />}
              {activeTab === 'scheduler' && <SchedulerPage key="scheduler" />}
              {activeTab === 'contact-scheduler' && <ContactSchedulerAdmin key="contact-scheduler" />} // ADD THIS

              {activeTab === 'features' && <FeaturesManagement key="features" />}
              {activeTab === 'system' && <SystemPage key="system" />}
              {activeTab === 'access-codes' && <AccessCodesPage key="access-codes" />}
              {activeTab === 'ai' && <AIManagementPage key="ai" />}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;